<script lang="ts" setup>
  import { useImageLoader } from '~/composables/image'

  const props = withDefaults(
    defineProps<{
      alt?: string
      src: string
      loading?: 'lazy' | 'eager'
      preload?: boolean
      aspectRatio?: string
      borderRadius?: string
      fit?: 'cover' | 'contain' | 'fill' | 'inside' | 'outside'
      canShow?: boolean
    }>(),
    {
      alt: '',
      canShow: true,
      loading: 'lazy',
      fit: undefined,
      preload: false,
      aspectRatio: undefined,
      borderRadius: undefined
    }
  )

  const emits = defineEmits<{
    (event: 'load'): void
  }>()

  const imageError = ref(false)

  const { hasImageLoaded, onImageLoad, resetLoadState } = useImageLoader()

  function handleImageLoad() {
    emits('load')

    if (props.canShow) {
      onImageLoad()
    }
  }

  watch(
    () => props.canShow,
    (newVal) => {
      if (newVal && hasImageLoaded.value) {
        onImageLoad()
      }
    }
  )

  watch(
    () => props.src,
    () => {
      resetLoadState()
    }
  )

  function onImageError() {
    resetLoadState()
    imageError.value = true
  }
</script>

<template>
  <div class="c-lazy-image">
    <Skeleton v-if="!hasImageLoaded" :border-radius="borderRadius" height="100%" width="100%" />

    <nuxt-img
      :alt="alt"
      :class="['lazy-image', { 'lazy-image--loaded': hasImageLoaded }]"
      :fit
      :loading
      :preload
      :src="imageError ? `/assets/icons/logo-circle.svg` : props.src"
      @error="onImageError"
      @load="handleImageLoad"
    />
  </div>
</template>

<style lang="scss" scoped>
  .c-lazy-image {
    .lazy-image {
      aspect-ratio: v-bind(aspectRatio);
      border-radius: v-bind(borderRadius);
      display: block;
      width: 100%;
      height: auto;
      opacity: 0;
      transition: opacity 0.3s;
    }

    .lazy-image--loaded {
      opacity: 1;
    }
  }
</style>
